// frontend/src/components/orcamento/useProjecao.js
// Estado e contas do Orçamento com Monte Carlo. Toda a interação é local:
// o backend manda percentis + vetores de 2.000 totais (despesas, receitas e
// resultado, com as MESMAS iterações), e aqui calculamos edição, pilares,
// chance de prejuízo, meta e cenários sem nova chamada.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { notificar } from '../../utils/notify';

const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });

// as duas naturezas do orçamento; as edições ficam separadas por natureza
// (o mesmo código de categoria pode existir nas duas)
export const NATUREZAS = ['despesa', 'receita'];
const VAZIO = { despesa: {}, receita: {} };

// Texto digitado → número. Vírgula e ponto juntos: o separador que vem por último
// é o decimal ("1.234,56" ou "1,234.56"). Só vírgula: vírgula decimal. Só ponto:
// "1234.5"/"1234.56" tem ponto decimal; fora isso, pontos são milhar ("1.234.567").
// Texto inválido → null.
export const parseBR = (txt) => {
  if (txt === null || txt === undefined) return null;
  if (typeof txt === 'number') return Number.isFinite(txt) ? txt : null;
  const s = String(txt).replace(/R\$|\s/g, '');
  if (s === '') return null;
  let norm;
  if (s.includes(',') && s.includes('.') && s.lastIndexOf('.') > s.lastIndexOf(',')) norm = s.replace(/,/g, '');
  else if (s.includes(',')) norm = s.replace(/\./g, '').replace(',', '.');
  else if (/^-?\d+\.\d{1,2}$/.test(s)) norm = s;
  else norm = s.replace(/\./g, '');
  const n = Number(norm);
  return Number.isFinite(n) ? n : null;
};

const temChave = (obj, k) => !!obj && Object.prototype.hasOwnProperty.call(obj, k);

// categorias criadas pelas premissas (custo extra, investimentos, eventos): não existem
// no plano de contas, então não vão para orcamento_mensal
export const ehSintetica = (c) => !!c?.sintetica || String(c?.codigo || '').startsWith('premissa:');
// somente leitura na grade: sintéticas e as de valor fixo definido nas premissas
export const ehSomenteLeitura = (c) => ehSintetica(c) || !!c?.valor_fixo;

// categorias de uma natureza no payload (despesa na raiz, receita em `receitas`)
export const categoriasDe = (proj, natureza) =>
  (natureza === 'receita' ? proj?.receitas?.categorias : proj?.categorias) || [];

// percentil de um vetor já ordenado
const percentilDe = (ord, q) =>
  (ord.length ? ord[Math.min(ord.length - 1, Math.floor((q / 100) * ord.length))] : 0);

// vetor simulado deslocado pelas edições (muda a posição, não a forma) + percentil
function useDistribuicao(vetor, desloc) {
  const simulado = useMemo(() => (vetor || []).map((x) => x + desloc), [vetor, desloc]);
  const ordenado = useMemo(() => [...simulado].sort((x, y) => x - y), [simulado]);
  const percentil = useCallback((q) => percentilDe(ordenado, q), [ordenado]);
  return { simulado, percentil };
}

// consolidar: visão do grupo (25 + 27), somente leitura — GET com ?consolidar=1
export default function useProjecao(clienteId, ano, consolidar = false) {
  const [proj, setProj] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  // 400 "A base já cobre <ano>": o ano pedido já está no histórico (a tela oferece o seguinte)
  const [anoCoberto, setAnoCoberto] = useState(false);
  // edicoes[natureza][codigo][mes] = número (valor digitado) | null (célula limpa pelo usuário)
  const [edicoes, setEdicoes] = useState(VAZIO);
  const [salvando, setSalvando] = useState(false);
  // id da última requisição: resposta de cliente/ano anterior é descartada
  const req = useRef(0);
  // cliente|ano atuais: um ajuste que termina depois da troca não recarrega a tela nova
  const chaveRef = useRef(`${clienteId}|${ano}|${consolidar ? 1 : 0}`);
  chaveRef.current = `${clienteId}|${ano}|${consolidar ? 1 : 0}`;

  // troca de cliente ou de ano: zera projeção e edições (recalcular NÃO zera as edições)
  useEffect(() => {
    setProj(null); setEdicoes(VAZIO); setErro(''); setAnoCoberto(false);
  }, [clienteId, ano, consolidar]);

  const carregar = useCallback((recalcular = false) => {
    if (!clienteId || !ano) return;
    const id = ++req.current;
    setCarregando(true); setErro(''); setAnoCoberto(false);
    axios.get(`${API}/api/orcamento-projecao/`, {
      params: { cliente_id: clienteId, ano, ...(recalcular ? { recalcular: 1 } : {}), ...(consolidar ? { consolidar: 1 } : {}) },
      headers: headers(), timeout: 180000,
    }).then((r) => {
      if (id !== req.current) return;
      if (!r.data?.success) { setErro(r.data?.erro || 'Falha ao projetar'); return; }
      setProj(r.data);
    }).catch((e) => {
      if (id !== req.current) return;
      const msg = e.response?.data?.erro || 'Erro ao carregar a projeção';
      setErro(msg);
      setAnoCoberto(e.response?.status === 400 && String(msg).startsWith('A base já cobre'));
    }).finally(() => { if (id === req.current) setCarregando(false); });
  }, [clienteId, ano, consolidar]);
  useEffect(() => { carregar(); }, [carregar]);

  const meses = useMemo(() => proj?.horizonte || [], [proj]);

  // valor que vale para a célula: edição do usuário > P50 > nada
  const valorCelula = useCallback((natureza, cat, mes) => {
    // célula somente leitura: vale o que veio das premissas (edição antiga não vence)
    const ed = (ehSomenteLeitura(cat) || proj?.somente_leitura) ? null : edicoes[natureza]?.[cat.codigo];
    if (temChave(ed, mes)) return ed[mes];
    const m = (cat.meses || []).find((x) => x.mes === mes);
    return m ? m.p50 : null;
  }, [edicoes, proj]);

  const editar = useCallback((natureza, codigo, mes, valor) => {
    setEdicoes((e) => {
      const lado = e[natureza] || {};
      return { ...e, [natureza]: { ...lado, [codigo]: { ...(lado[codigo] || {}), [mes]: valor } } };
    });
  }, []);
  const limparEdicoes = useCallback(() => setEdicoes(VAZIO), []);

  // totais por mês e no ano de cada natureza, já com as edições, e o deslocamento
  // de cada lado (total editado − Σ total_p50 "puro")
  const totais = useMemo(() => {
    const out = {};
    NATUREZAS.forEach((nat) => {
      const porMes = {}; let anoTot = 0; let p50Base = 0;
      categoriasDe(proj, nat).forEach((c) => {
        p50Base += c.total_p50 || 0;
        meses.forEach((m) => {
          const v = valorCelula(nat, c, m) || 0; porMes[m] = (porMes[m] || 0) + v; anoTot += v;
        });
      });
      out[nat] = { porMes, ano: anoTot, deslocamento: anoTot - p50Base };
    });
    const porMesRes = {};
    meses.forEach((m) => { porMesRes[m] = (out.receita.porMes[m] || 0) - (out.despesa.porMes[m] || 0); });
    out.resultado = { porMes: porMesRes, ano: out.receita.ano - out.despesa.ano };
    return out;
  }, [proj, meses, valorCelula]);

  // pilares: pilares[dep] = { receita, despesa } = Σ_cat Σ_mes valor × fração do departamento
  // (união dos departamentos das duas naturezas; '' = sem departamento)
  const pilares = useMemo(() => {
    const acc = {};
    NATUREZAS.forEach((nat) => categoriasDe(proj, nat).forEach((c) => {
      const tot = meses.reduce((s, m) => s + (valorCelula(nat, c, m) || 0), 0);
      Object.entries(c.rateio_pilar || { '': 1 }).forEach(([dep, frac]) => {
        acc[dep] = acc[dep] || { receita: 0, despesa: 0 };
        acc[dep][nat] += tot * frac;
      });
    }));
    return acc;
  }, [proj, meses, valorCelula]);

  // distribuições deslocadas pelas edições; resultado desloca por Δreceitas − Δdespesas
  const dDesp = totais.despesa.deslocamento;
  const dRec = totais.receita.deslocamento;
  const despesas = useDistribuicao(proj?.total_simulado, dDesp);
  const receitas = useDistribuicao(proj?.receitas?.total_simulado, dRec);
  const resultado = useDistribuicao(proj?.resultado?.total_simulado, dRec - dDesp);
  const vetorRes = resultado.simulado;
  // chance de prejuízo: fração dos cenários com resultado anual < 0
  const probPrejuizo = useMemo(
    () => (vetorRes.length ? vetorRes.filter((x) => x < 0).length / vetorRes.length : null), [vetorRes]);
  // chance de bater a meta: fração dos cenários com resultado ≥ meta
  const chanceMeta = useCallback((meta) => {
    if (!vetorRes.length || !Number.isFinite(meta)) return null;
    return vetorRes.filter((x) => x >= meta).length / vetorRes.length;
  }, [vetorRes]);

  // Deslocamento das edições por pilar e natureza:
  // Δ[dep][nat] = Σ_cat (total editado da categoria − total_p50) × rateio_pilar[cat][dep]
  const deltaPilares = useMemo(() => {
    const acc = {};
    NATUREZAS.forEach((nat) => categoriasDe(proj, nat).forEach((c) => {
      if (!edicoes[nat]?.[c.codigo]) return;   // sem edição: Δ = 0
      const tot = meses.reduce((s, m) => s + (valorCelula(nat, c, m) || 0), 0);
      const d = tot - (c.total_p50 || 0);
      if (!d) return;
      Object.entries(c.rateio_pilar || { '': 1 }).forEach(([dep, frac]) => {
        acc[dep] = acc[dep] || { receita: 0, despesa: 0 };
        acc[dep][nat] += d * frac;
      });
    }));
    return acc;
  }, [proj, meses, valorCelula, edicoes]);

  // Cenário por pilar (payload `pilares_simulados`, opcional): a amostra do resultado
  // de cada pilar é deslocada por Δreceita − Δdespesa do pilar e os percentis e a
  // chance de prejuízo são recalculados aqui (cada amostra é ordenada uma vez).
  const pilaresSimulados = useMemo(() => {
    const lista = Array.isArray(proj?.pilares_simulados) ? proj.pilares_simulados : [];
    return lista.map((pl) => {
      const cod = pl.codigo === null || pl.codigo === undefined ? '' : String(pl.codigo);
      const d = deltaPilares[cod] || { receita: 0, despesa: 0 };
      const desloc = d.receita - d.despesa;
      const amostra = Array.isArray(pl.resultado_simulado) ? pl.resultado_simulado : [];
      const ord = amostra.map((x) => x + desloc).sort((x, y) => x - y);
      const base = pl.resultado || {};
      const res = ord.length
        ? { p10: percentilDe(ord, 10), p50: percentilDe(ord, 50), p90: percentilDe(ord, 90) }
        : { p10: (base.p10 ?? 0) + desloc, p50: (base.p50 ?? 0) + desloc, p90: (base.p90 ?? 0) + desloc };
      // fração < 0 no vetor ordenado: posição do primeiro valor ≥ 0
      let neg = 0;
      while (neg < ord.length && ord[neg] < 0) neg += 1;
      const prob = ord.length ? neg / ord.length : (Number.isFinite(pl.prob_prejuizo) ? pl.prob_prejuizo : null);
      return {
        codigo: cod, nome: pl.nome || (cod ? cod : 'Sem departamento'),
        receitaP50: (pl.receita?.p50 || 0) + d.receita, despesaP50: (pl.despesa?.p50 || 0) + d.despesa,
        resultado: res, probPrejuizo: prob,
      };
    });
  }, [proj, deltaPilares]);

  // Composição de um pilar: por natureza, as categorias que o alimentam, com
  // valor no pilar = total anual da grade (edições incluídas) × rateio_pilar[dep].
  // valor null = categoria vermelha/decisão ainda sem valor na grade.
  // Valores que arredondam a zero ficam de fora. Ordenado do maior para o menor.
  const composicaoPilar = useCallback((dep) => {
    const out = {};
    NATUREZAS.forEach((nat) => {
      const itens = [];
      categoriasDe(proj, nat).forEach((c) => {
        const frac = (c.rateio_pilar || { '': 1 })[dep];
        if (!(frac > 0)) return;
        const vals = meses.map((m) => valorCelula(nat, c, m));
        const vazio = vals.every((v) => v === null || v === undefined);
        if (vazio && (c.selo === 'vermelho' || c.selo === 'decisao')) {
          itens.push({ codigo: c.codigo, nome: c.nome, cat: c, valor: null });
          return;
        }
        const valor = vals.reduce((s, v) => s + (v || 0), 0) * frac;
        if (Math.round(valor) === 0) return;
        itens.push({ codigo: c.codigo, nome: c.nome, cat: c, valor });
      });
      itens.sort((a, b) => (b.valor ?? -Infinity) - (a.valor ?? -Infinity));
      out[nat] = itens;
    });
    return out;
  }, [proj, meses, valorCelula]);

  // ajustes (decisão / outlier): falha avisa e não recarrega; devolve true/false
  const ajustar = useCallback(async (corpo) => {
    if (proj?.somente_leitura) return false;      // consolidado: o backend recusa gravações
    const chave = chaveRef.current;
    try {
      await axios.post(`${API}/api/orcamento-projecao-ajuste/`, { cliente_id: clienteId, ...corpo }, { headers: headers() });
    } catch (e) {
      notificar('Não foi possível registrar o ajuste'); return false;
    }
    if (chave !== chaveRef.current) return false;
    carregar(true); return true;
  }, [clienteId, proj, carregar]);

  const marcarDecisao = useCallback((natureza, codigo, desfazer = false) =>
    ajustar({ natureza, codigo_categoria: codigo, acao: desfazer ? 'desfazer_decisao' : 'decisao' }), [ajustar]);

  const ignorarOutlier = useCallback((natureza, codigo, mes, reconsiderar = false) =>
    ajustar({ natureza, codigo_categoria: codigo, acao: reconsiderar ? 'reconsiderar_outlier' : 'ignorar_outlier', referencia: mes }), [ajustar]);

  // Premissas do ano: grava e, se deu certo e o cliente/ano não mudou, refaz o GET
  // (cache do backend é por hash das premissas; as edições da grade ficam).
  // Devolve {ok, premissas} ou {ok:false, erro, campo}.
  const salvarPremissas = useCallback(async (premissas) => {
    const chave = chaveRef.current;
    try {
      const r = await axios.post(`${API}/api/orcamento-projecao-premissas/`,
        { cliente_id: clienteId, ano, premissas }, { headers: headers() });
      if (!r.data?.success) return { ok: false, erro: r.data?.erro || 'Falha ao gravar as premissas', campo: r.data?.campo };
      return { ok: true, premissas: r.data.premissas, atual: chave === chaveRef.current };
    } catch (e) {
      return { ok: false, erro: e.response?.data?.erro || 'Não foi possível gravar as premissas', campo: e.response?.data?.campo };
    }
  }, [clienteId, ano]);

  // Grava só valores numéricos ≥ 0 (o backend rejeita o lote inteiro se vier negativo/não numérico).
  // Por natureza, duas chamadas: o que o usuário digitou vai como 'manual'; o resto
  // (projeção aceita) como 'p50'. Até 4 POSTs; para no primeiro que falhar.
  const salvar = useCallback(async () => {
    if (!proj) return { ok: false };
    if (proj.somente_leitura) return { ok: false, erro: 'Visão consolidada é só leitura' };
    if (proj.cliente_id !== Number(clienteId) || proj.ano !== Number(ano)) {
      return { ok: false, erro: 'Projeção desatualizada; recarregue' };
    }
    setSalvando(true);
    let gravados = 0;
    try {
      for (const natureza of NATUREZAS) {
        const manuais = [], projetados = [];
        categoriasDe(proj, natureza).filter((c) => !ehSintetica(c)).forEach((c) => meses.forEach((m) => {
          const v = valorCelula(natureza, c, m);
          if (!(Number.isFinite(v) && v >= 0)) return;
          const item = { codigo_categoria: c.codigo, mes_referencia: m, valor: v };
          (temChave(edicoes[natureza]?.[c.codigo], m) ? manuais : projetados).push(item);
        }));
        for (const [modo, itens] of [['p50', projetados], ['manual', manuais]]) {
          if (!itens.length) continue;
          const r = await axios.post(`${API}/api/orcamento-aplicar-projecao/`,
            { cliente_id: clienteId, ano, natureza, modo, itens }, { headers: headers() });
          if (!r.data?.success) return { ok: false, gravados, erro: r.data?.erro || 'Falha ao gravar' };
          gravados += r.data?.gravados || 0;
        }
      }
      return { ok: true, gravados };
    } catch (e) {
      return { ok: false, gravados, erro: e.response?.data?.erro || 'Erro ao gravar' };
    } finally { setSalvando(false); }
  }, [proj, meses, valorCelula, edicoes, clienteId, ano]);

  return { proj, carregando, erro, anoCoberto, meses, edicoes, valorCelula, editar, limparEdicoes,
           totais, pilares, despesas, receitas, resultado, probPrejuizo, chanceMeta, pilaresSimulados, composicaoPilar,
           marcarDecisao, ignorarOutlier, salvar, salvando, salvarPremissas,
           recarregar: () => carregar(true), atualizar: () => carregar() };
}
